import { useClipboard } from "@mantine/hooks"
import { useTranslations } from "next-intl"
import { useTheme } from "next-themes"
import type { Dispatch, SetStateAction } from "react"
import { useMemo } from "react"
import { useForm } from "react-hook-form"
import { Button } from "~/components/legacy/common/button"
import { Card } from "~/components/legacy/common/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/legacy/common/dialog"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/legacy/common/form"
import { Input } from "~/components/legacy/common/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/legacy/common/select"
import { TextArea } from "~/components/legacy/common/textarea"
import { CheckIcon, ClipboardIcon } from "~/components/ui/icons"
import { getToolBadgeEmbedCode, getToolBadgeUrl } from "~/lib/tool-badge-embed"
import { cx } from "~/lib/legacy-utils"
import type { ToolOne } from "~/server/web/tools/payloads"

const THEMES = ["light", "dark"] as const

type Theme = (typeof THEMES)[number]

type EmbedForm = {
  theme: Theme
  width: number
  height: number
}

type ToolEmbedDialogProps = {
  tool: ToolOne
  isOpen: boolean
  setIsOpen: Dispatch<SetStateAction<boolean>>
}

export const ToolEmbedDialog = ({ tool, isOpen, setIsOpen }: ToolEmbedDialogProps) => {
  const { resolvedTheme } = useTheme()
  const clipboard = useClipboard({ timeout: 2000 })
  const t = useTranslations("dialogs.embed")
  const tCommon = useTranslations("common")

  const form = useForm<EmbedForm>({
    defaultValues: { theme: resolvedTheme as Theme, width: 200, height: 50 },
    mode: "onChange",
  })

  const [theme, width, height] = form.watch(["theme", "width", "height"])

  const badgeUrl = useMemo(
    () => getToolBadgeUrl(tool, { theme, width, height }),
    [theme, width, height, tool],
  )

  const embedCode = useMemo(
    () => getToolBadgeEmbedCode(tool, { theme, width, height }),
    [theme, width, height, tool],
  )

  const previewBg = theme === "dark" ? "bg-black" : "bg-white"

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("title", { toolName: tool.name })}</DialogTitle>
          <DialogDescription>{t("description")}</DialogDescription>
          <p className="text-sm/normal text-muted-foreground">{t("policy_note")}</p>
        </DialogHeader>

        <Form {...form}>
          <form
            className="grid w-full gap-x-3 gap-y-5 sm:grid-cols-3"
            autoComplete="off"
            onSubmit={e => e.preventDefault()}
          >
            <FormField
              control={form.control}
              name="theme"
              render={({ field: { onChange, value, ...field } }) => (
                <FormItem>
                  <FormLabel>{t("theme_label")}</FormLabel>
                  <FormControl>
                    <Select value={value} onValueChange={onChange} {...field}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {THEMES.map(theme => (
                          <SelectItem key={theme} value={theme}>
                            {tCommon(`themes.${theme}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="width"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("width_label")}</FormLabel>
                  <FormControl>
                    <Input type="number" min={100} max={600} step={10} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="height"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("height_label")}</FormLabel>
                  <FormControl>
                    <Input type="number" min={30} max={200} step={5} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="col-span-full">
              <div className="text-xs mb-1 text-foreground/60">{t("preview_label")}</div>
              <Card
                className={cx(
                  "flex items-center justify-center min-h-20 bg-background overflow-clip",
                  previewBg,
                )}
                hover={false}
              >
                <img
                  src={badgeUrl}
                  width={width}
                  height={height}
                  alt={`${tool.name} badge preview`}
                  className="block"
                  draggable={false}
                />
              </Card>
            </div>

            <div className="col-span-full">
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="text-xs text-foreground/60">{t("code_label")}</div>

                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={() => clipboard.copy(embedCode)}
                  prefix={clipboard.copied ? <CheckIcon /> : <ClipboardIcon />}
                  className={cx(clipboard.copied && "text-green-600")}
                >
                  {clipboard.copied ? t("copied") : t("copy_button")}
                </Button>
              </div>

              <TextArea
                value={embedCode}
                readOnly
                className="block font-mono text-xs cursor-pointer"
                onFocus={e => e.target.select()}
              />
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
